/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

import { useUiOptions } from "../ui-options-context";

type Validator = {
  validate: () => boolean;
  onInvalid: () => void;
};

const focusableSelector =
  [
    "input:not([disabled]):not([type=hidden])",
    "textarea:not([disabled])",
    "select:not([disabled])",
    "button:not([disabled])",
    '[tabindex]:not([disabled]):not([tabindex="-1"])',
  ].join(":not([hidden]),") + ":not([hidden])";

const scrollAndFocusInvalidField = (form: HTMLFormElement) => {
  const invalidElement = form.querySelector<HTMLElement>(
    '[aria-invalid="true"]',
  );

  if (!invalidElement) {
    return;
  }

  invalidElement.scrollIntoView({
    block: "center",
    behavior: "smooth",
  });

  const focusTarget = invalidElement.matches(focusableSelector)
    ? invalidElement
    : invalidElement.querySelector<HTMLElement>(focusableSelector);

  focusTarget?.focus({ preventScroll: true });
};

const FormContext = createContext<
  MutableRefObject<Map<unknown, Validator>> | undefined
>(undefined);

/**
 * Form component that prevents submission until every field is valid.
 *
 * If multiple fields share the same state object,
 * consumers must use functional state updates to avoid race conditions.
 */
export const ExperimentalForm = ({
  onSubmit,
  children,
}: {
  /**
   * A callback function that is invoked when the form is submitted and all form fields are valid.
   */
  onSubmit: () => void;
  /**
   * Content of form
   */
  children: ReactNode;
}) => {
  const { experimental } = useUiOptions();
  const validators = useRef(new Map<unknown, Validator>());
  const formRef = useRef<HTMLFormElement>(null);
  const [invalidSubmitId, setInvalidSubmitId] = useState<{}>();

  useEffect(() => {
    if (!invalidSubmitId || !formRef.current) {
      return;
    }

    scrollAndFocusInvalidField(formRef.current);
  }, [invalidSubmitId]);

  return (
    <form
      ref={formRef}
      css={{ display: "contents" }}
      onSubmit={(e) => {
        e.preventDefault();

        let isValid = true;

        for (const { validate, onInvalid } of validators.current.values()) {
          if (!validate()) {
            isValid = false;
            onInvalid();
          }
        }

        if (isValid) {
          onSubmit();
          return;
        }

        if (experimental?.enableErrorFocusOnSubmit) {
          setInvalidSubmitId({});
        }
      }}
    >
      <FormContext.Provider value={validators}>{children}</FormContext.Provider>
    </form>
  );
};

/**
 * Registers a field in `ExperimentalForm` and shows error before submit.
 */
export const useSubmitGuard = ({
  validate,
  onInvalid,
}: {
  /**
   * A function that returns `true` if the field is valid.
   */
  validate: () => boolean;
  /**
   * Callback the form calls when the field is invalid.
   */
  onInvalid: () => void;
}) => {
  const [id] = useState({});
  const validatorsRef = useContext(FormContext);

  useEffect(() => {
    if (!validatorsRef) {
      return;
    }

    const validators = validatorsRef.current;

    validators.set(id, { validate, onInvalid });

    return () => {
      validators.delete(id);
    };
  }, [validate, validatorsRef, onInvalid, id]);
};
